import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { usd } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gtax/telegram/admin`;
const BADGE = { pending: "bg-[#E8DDC4] text-[#6B5F4B]", approved: "bg-[#8F6B22] text-white", rejected: "bg-[#A0523F] text-white" };
const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Something went wrong."; };

const BotCard = ({ s, h, reload }) => {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try { await axios.post(`${API}/setup`, { public_url: window.location.origin }, h); toast.success("Bot connected to this site"); reload(); } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const [adm, setAdm] = useState(null);
  const link = async () => {
    try { const { data } = await axios.post(`${API}/link-code`, {}, h); setAdm(data.link); } catch (e) { toast.error(err(e)); }
  };
  const row = (k, v, id) => <div className="flex justify-between py-2 border-b border-[color:var(--line)] text-sm"><span className="text-[color:var(--mute)]">{k}</span><span data-testid={id}>{v}</span></div>;
  return (
    <div className="gx-card p-8 max-w-2xl space-y-6" data-testid="gtax-tg-bot-card">
      <h2 className="gx-serif text-3xl">Telegram bot</h2>
      <div>
        {row("Bot token", s.configured ? "Added" : "Not added yet", "gtax-tg-configured")}
        {row("Bot", s.bot_username ? `@${s.bot_username}` : "—", "gtax-tg-username")}
        {row("Connected to", s.public_url || "—", "gtax-tg-public-url")}
        {row("Admin alerts chat", s.admin_linked ? "Linked" : "Not linked", "gtax-tg-admin-linked")}
      </div>
      <div className="flex flex-wrap gap-3">
        <button disabled={!s.configured || busy} onClick={connect} className="gx-btn disabled:opacity-40" data-testid="gtax-tg-connect">{busy ? "Connecting…" : "Connect bot to this site"}</button>
        <button disabled={!s.bot_username} onClick={link} className="gx-ghost disabled:opacity-40" data-testid="gtax-tg-link-admin">Link my Telegram for alerts</button>
      </div>
      {adm && (
        <div className="p-4 border border-[color:var(--gold)] space-y-3" data-testid="gtax-tg-admin-link-box">
          <p className="text-sm">Open this link on the phone with your Telegram and press <b>Start</b>. Valid for 10 minutes.</p>
          <a href={adm} target="_blank" rel="noreferrer" className="gx-btn inline-block" data-testid="gtax-tg-admin-link-open">Open in Telegram</a>
          <p className="text-xs break-all text-[color:var(--mute)] select-all" data-testid="gtax-tg-admin-link-text">{adm}</p>
          <button className="gx-ghost" onClick={() => navigator.clipboard.writeText(adm).then(() => toast.success("Link copied"))} data-testid="gtax-tg-admin-link-copy">Copy link</button>
        </div>
      )}
      {s.public_url && s.public_url !== window.location.origin && <p className="text-sm text-[#A0523F]" data-testid="gtax-tg-other-site">The bot currently points to another site. Press Connect to point it here.</p>}
    </div>
  );
};

const NETS = [["usdc_erc20", "USDC · Ethereum (ERC-20)"], ["usdt_bep20", "USDT · BNB Smart Chain (BEP-20)"], ["usdt_trc20", "USDT · Tron (TRC-20)"]];
const NET_LABEL = Object.fromEntries(NETS);
const usd2 = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const WalletsCard = ({ s, h, reload }) => {
  const [w, setW] = useState({});
  useEffect(() => { setW(s.wallets || {}); }, [s.wallets]);
  const save = async () => {
    try { await axios.put(`${API}/wallets`, w, h); toast.success("Telegram treasury wallets saved"); reload(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="gx-card p-8 max-w-2xl space-y-6" data-testid="gtax-tg-wallets-card">
      <h2 className="gx-serif text-3xl">Telegram treasury wallets</h2>
      <p className="text-sm text-[color:var(--mute)]">Sent in the bot to invited Telegram members right after they sign. The website keeps its own treasury address. Leave a network blank to hide it in the bot.</p>
      {NETS.map(([k, l]) => (
        <div key={k}>
          <label className="block text-xs tracking-widest uppercase text-[color:var(--mute)]">{l}</label>
          <input value={w[k] || ""} onChange={(e) => setW({ ...w, [k]: e.target.value })} className="w-full py-3 font-mono text-sm" data-testid={`gtax-tg-wallet-${k}`} />
        </div>
      ))}
      <button onClick={save} className="gx-btn" data-testid="gtax-tg-wallets-save">Save wallets</button>
    </div>
  );
};

const Chain = ({ d, h, onResult }) => {
  const [busy, setBusy] = useState(false);
  const c = d.chain;
  const run = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/deposits/check`, { reference: d.reference, txid: d.txid }, h); onResult(data); } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const ok = c?.status === "received";
  const tone = ok ? "text-[#8F6B22] font-semibold" : c?.status === "unsupported" ? "text-[color:var(--mute)]" : "text-[#A0523F]";
  return (
    <div className="text-xs space-y-1 max-w-[220px]" data-testid={`gtax-tg-dep-chain-${d.txid}`}>
      {c && <div className={tone} data-testid={`gtax-tg-dep-chain-status-${d.txid}`}>
        {ok ? `${usd2(c.amount)} received${c.confirmations != null ? ` · ${c.confirmations} conf.` : ""}` : c.message}
      </div>}
      {c?.explorer && <a href={c.explorer} target="_blank" rel="noreferrer" className="underline text-[color:var(--mute)]" data-testid={`gtax-tg-dep-explorer-${d.txid}`}>View on explorer</a>}
      {d.network && !d.confirmed && <button onClick={run} disabled={busy} className="block underline" data-testid={`gtax-tg-dep-check-${d.txid}`}>{busy ? "Checking…" : c ? "Check again" : "Check on-chain"}</button>}
      {!d.network && !c && <span className="text-[color:var(--mute)]">Manual check</span>}
    </div>
  );
};

const Deposits = ({ rows, h, reload }) => {
  const [amt, setAmt] = useState({});
  const [chain, setChain] = useState({});
  useEffect(() => {
    const pre = {};
    rows.forEach((d) => { if (d.chain?.status === "received") pre[d.txid] = d.chain.amount; });
    setAmt((a) => ({ ...pre, ...a }));
  }, [rows]);
  const onResult = (d, c) => {
    setChain({ ...chain, [d.txid]: c });
    if (c.status === "received") setAmt({ ...amt, [d.txid]: c.amount });
  };
  const confirm = async (d) => {
    const amount = Number(amt[d.txid]);
    if (!(amount > 0)) return toast.error("Enter the amount received");
    if (!window.confirm(`Confirm ${usd2(amount)} received from ${d.full_name}? This credits their ledger and notifies them.`)) return;
    try { await axios.post(`${API}/deposits/confirm`, { reference: d.reference, txid: d.txid, amount }, h); toast.success("Deposit confirmed"); reload(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <div data-testid="gtax-tg-deposits">
      <h2 className="gx-serif text-3xl">Deposits ({rows.filter((d) => !d.confirmed).length} to confirm)</h2>
      <p className="text-sm text-[color:var(--mute)] mt-2">Check the transaction on-chain (Telegram deposits are checked automatically when submitted), review the amount received and confirm. The member's ledger is credited and they get a confirmation in Telegram.</p>
      <div className="overflow-x-auto mt-6">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-[color:var(--mute)] border-b border-[color:var(--line)]"><th>Sent</th><th>Who paid</th><th>Network</th><th>TXID</th><th>On-chain</th><th className="text-right">Intended</th><th>Received</th></tr></thead>
          <tbody>
            {rows.map((d, i) => (
              <tr key={`${d.reference}-${d.txid}-${i}`} className="border-b border-[color:var(--line)] align-top" data-testid={`gtax-tg-dep-row-${d.txid}`}>
                <td className="text-xs">{new Date(d.at).toLocaleString()}</td>
                <td>{d.full_name}<div className="text-xs text-[color:var(--mute)]"><span className="font-mono">{d.reference}</span>{d.telegram_username ? ` · @${d.telegram_username}` : ""} · {d.phone} · {d.channel}</div></td>
                <td className="text-xs">{NET_LABEL[d.network] || d.network || "—"}</td>
                <td className="font-mono text-xs break-all max-w-[200px]">{d.txid}</td>
                <td><Chain d={{ ...d, chain: chain[d.txid] || d.chain }} h={h} onResult={(c) => onResult(d, c)} /></td>
                <td className="text-right">{usd(d.intended || 0)}</td>
                <td className="whitespace-nowrap">
                  {d.confirmed ? <span className="px-3 py-1 rounded-full text-xs uppercase tracking-widest bg-[#8F6B22] text-white" data-testid={`gtax-tg-dep-confirmed-${d.txid}`}>{usd2(d.confirmed_amount)} confirmed</span> : (
                    <span className="flex gap-2 items-center">
                      <input type="number" placeholder="USD" value={amt[d.txid] || ""} onChange={(e) => setAmt({ ...amt, [d.txid]: e.target.value })} className="w-28 py-1" data-testid={`gtax-tg-dep-amount-${d.txid}`} />
                      <button onClick={() => confirm(d)} className="gx-ghost !px-3 !py-1" data-testid={`gtax-tg-dep-confirm-${d.txid}`}>Confirm</button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Withdrawals = ({ rows, h, reload }) => {
  const decide = async (w, status) => {
    if (!window.confirm(`${status === "approved" ? "Approve" : "Reject"} ${usd(w.amount)} for ${w.full_name}?`)) return;
    try { await axios.post(`${API}/withdrawals/${w.id}`, { status }, h); toast.success(`Withdrawal ${status}`); reload(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <div data-testid="gtax-tg-withdrawals">
      <h2 className="gx-serif text-3xl">Withdrawal requests ({rows.length})</h2>
      <p className="text-sm text-[color:var(--mute)] mt-2">Approving records a withdrawal in the returns ledger and notifies the member. You send the USDT yourself; nothing is paid automatically.</p>
      <div className="overflow-x-auto mt-6">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-[color:var(--mute)] border-b border-[color:var(--line)]"><th>Requested</th><th>Member</th><th className="text-right">Amount</th><th>Wallet</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.id} className="border-b border-[color:var(--line)] align-top" data-testid={`gtax-tg-wd-row-${w.id}`}>
                <td className="text-xs">{new Date(w.requested_at).toLocaleString()}</td>
                <td>{w.full_name}<div className="text-xs font-mono text-[color:var(--mute)]">{w.reference}</div></td>
                <td className="text-right">{usd(w.amount)}</td>
                <td className="font-mono text-xs break-all max-w-[200px]">{w.wallet}</td>
                <td><span className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest ${BADGE[w.status]}`} data-testid={`gtax-tg-wd-status-${w.id}`}>{w.status}</span></td>
                <td className="whitespace-nowrap space-x-2">
                  {w.status === "pending" && <>
                    <button onClick={() => decide(w, "approved")} className="gx-ghost !px-3 !py-1" data-testid={`gtax-tg-wd-approve-${w.id}`}>Approve</button>
                    <button onClick={() => decide(w, "rejected")} className="gx-ghost !px-3 !py-1" data-testid={`gtax-tg-wd-reject-${w.id}`}>Reject</button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AdminTelegram = ({ token }) => {
  const [s, setS] = useState({});
  const [rows, setRows] = useState([]);
  const [deps, setDeps] = useState([]);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const reload = () => Promise.all([axios.get(`${API}/status`, h), axios.get(`${API}/withdrawals`, h), axios.get(`${API}/deposits`, h)])
    .then(([a, b, c]) => { setS(a.data); setRows(b.data); setDeps(c.data); }).catch((e) => toast.error(err(e)));
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-14" data-testid="gtax-admin-telegram">
      <BotCard s={s} h={h} reload={reload} />
      <WalletsCard s={s} h={h} reload={reload} />
      <Deposits rows={deps} h={h} reload={reload} />
      <Withdrawals rows={rows} h={h} reload={reload} />
    </div>
  );
};
